import React, { useEffect, useState } from 'react';
import * as App from '../../utils/instalarApp';

/** Ofrecer el panel como app, en el teléfono y en el PC.
 *
 *  Dos piezas que comparten estado (useInstalarApp, desde PanelApp):
 *  - Una franja arriba del panel, hasta que la persona la cierre.
 *  - "Instalar app" en el menú, que queda siempre mientras no esté instalada:
 *    quien cerró la franja y después cambia de opinión tiene por dónde volver.
 *
 *  Nada de esto aparece si ya se está usando como app, ni donde el navegador
 *  no permite instalar (Firefox en el PC). Ver utils/instalarApp. */

type Forma = 'boton' | 'ios' | 'android' | 'mac';

const CLAVE_DESCARTADA = 'panel-app-descartada';

const formaActual = (): Forma | null => {
  if (App.esAppInstalada()) return null;
  if (App.puedeInstalarConBoton()) return 'boton';
  if (App.esIOS()) return 'ios';
  if (App.esAndroid()) return 'android';
  if (App.esSafariMac()) return 'mac';
  return null;
};

export function useInstalarApp() {
  const [, refrescar] = useState(0);
  const [descartada, setDescartada] = useState(() => {
    try { return localStorage.getItem(CLAVE_DESCARTADA) === '1'; } catch { return false; }
  });
  useEffect(() => App.alCambiarInstalacion(() => refrescar(n => n + 1)), []);

  const guardar = (v: boolean) => {
    setDescartada(v);
    try {
      if (v) localStorage.setItem(CLAVE_DESCARTADA, '1');
      else localStorage.removeItem(CLAVE_DESCARTADA);
    } catch { /* sin almacenamiento */ }
  };

  return {
    forma: formaActual(),
    descartada,
    descartar: () => guardar(true),
    /** Desde el menú: con botón abre el diálogo del navegador; sin botón
     *  vuelve a mostrar la franja, que es donde está explicado el camino. */
    pedir: (forma: Forma) => { if (forma === 'boton') void App.instalar(); else guardar(false); },
  };
}

const TEXTOS: Record<Forma, React.ReactNode> = {
  boton: <>Ábrelo con un clic desde tu escritorio o tu pantalla de inicio, en su propia ventana.</>,
  ios: <>Toca <b>Compartir</b> <span aria-hidden>⬆︎</span> y luego <b>«Agregar a inicio»</b>. Queda con su ícono, a pantalla completa.</>,
  android: <>Abre el menú <b>⋮</b> del navegador y elige <b>«Instalar app»</b> o <b>«Agregar a la pantalla principal»</b>.</>,
  mac: <>En Safari, abre el menú <b>Archivo</b> y elige <b>«Agregar al Dock»</b>. Queda en su propia ventana.</>,
};

export const AvisoInstalarApp: React.FC<{ app: ReturnType<typeof useInstalarApp> }> = ({ app }) => {
  if (!app.forma || app.descartada) return null;
  return (
    <div className="panel-instalar">
      <span className="panel-instalar-texto">
        <strong>📲 Ten Tabularis como app.</strong> {TEXTOS[app.forma]}
      </span>
      {app.forma === 'boton' && (
        <button type="button" className="panel-instalar-btn" onClick={() => app.pedir('boton')}>Instalar</button>
      )}
      <button type="button" className="panel-instalar-cerrar" onClick={app.descartar} title="No mostrar más" aria-label="No mostrar más">×</button>
    </div>
  );
};

export const BotonInstalarApp: React.FC<{ app: ReturnType<typeof useInstalarApp>; onUsar: () => void }> = ({ app, onUsar }) => {
  if (!app.forma) return null;
  const forma = app.forma;
  return (
    <button className="pnav-btn" onClick={() => { app.pedir(forma); onUsar(); }} title="Abre el panel como app, con su propio ícono">
      <span style={{ fontSize: 14 }}>📲</span>Instalar app
    </button>
  );
};
